// src/components/sections/OriginSection.js
import React from 'react';
import { Box, VStack, Text } from '@chakra-ui/react';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';

export default function OriginSection() {
  return (
    <SectionContainer bg="bg.900" id="origin" aria-labelledby="origin-heading">
      <VStack spacing={8} align="center" textAlign="center" maxW="780px" mx="auto">
        <ScrollReveal>
          <Text
            fontFamily="'JetBrains Mono', monospace"
            fontWeight="500"
            fontSize="0.75rem"
            textTransform="uppercase"
            letterSpacing="1.5px"
            color="brand.300"
          >
            The Name
          </Text>
        </ScrollReveal>

        {/* Hebrew letterform as a quiet hero element */}
        <ScrollReveal delay={0.05}>
          <Text
            fontSize={{ base: '3.5rem', md: '5rem' }}
            lineHeight="1"
            fontWeight="500"
            color="brand.400"
            opacity={0.9}
            dir="rtl"
            aria-label="Yetzira"
            sx={{ textShadow: '0 0 40px rgba(56,189,248,0.25)' }}
          >
            Yetzira
          </Text>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <Text
            id="origin-heading"
            as="h2"
            fontFamily="'Space Grotesk', sans-serif"
            fontWeight="700"
            fontSize={{ base: 'clamp(1.8rem, 5vw, 2rem)', md: 'clamp(2rem, 3.5vw, 2.6rem)' }}
            letterSpacing="-0.02em"
            color="text.primary"
            lineHeight="1.15"
          >
            Why we're called{' '}
            <Box as="span" color="brand.400">Yetzira</Box>
          </Text>
        </ScrollReveal>

        <ScrollReveal delay={0.15}>
          <VStack spacing={5} color="text.muted" fontSize="1.05rem" lineHeight="1.75">
            <Text>
              <Box as="i" color="text.primary">Yetzira</Box> is the word for{' '}
              <Box as="span" color="text.primary" fontWeight="600">formation</Box> — the act of
              bringing something into being.
            </Text>
            <Text>
              It traces to one of the oldest ideas in human thought: the{' '}
              <Box as="span" color="text.primary" fontWeight="600">Book Yetzirah</Box>, the{' '}
              <Box as="i">Book of Formation</Box>, which describes how an entire universe is created
              from a small set of elemental building blocks — combined and recombined into infinite
              complexity.
            </Text>
            <Text>
              That is exactly what we build. A handful of simple, specialized agents — orchestrated
              and recombined — into organizations of limitless capability.
            </Text>
            <Text>
              The root of the word, <Box as="i" color="text.primary">yatzar</Box>, means{' '}
              <Box as="span" color="text.primary" fontWeight="600">to shape</Box>, as a potter
              shapes clay. It is the word for the creative impulse itself: the drive to make.
            </Text>
          </VStack>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <Box borderTop="1px solid" borderColor="whiteAlpha.100" pt={8} w="100%" maxW="520px">
            <Text color="text.muted" fontSize="1.05rem" mb={2}>
              We chose it because yetzira.ai doesn't just create.
            </Text>
            <Text
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="700"
              fontStyle="italic"
              fontSize={{ base: '1.4rem', md: '1.9rem' }}
              letterSpacing="-0.02em"
              color="brand.400"
            >
              It creates things that create.
            </Text>
          </Box>
        </ScrollReveal>
      </VStack>
    </SectionContainer>
  );
}
